// AI generated
import type { Node, NodeAddChange, NodeChange } from '@xyflow/react'

/** Index the nodes by id, at most once per call and only once a lookup needs it. */
const nodeLookup = (nodes: Node[]) => {
  let byId: Map<string, Node> | undefined

  return (id: string) => {
    byId ??= new Map(nodes.map((node) => [node.id, node]))
    return byId.get(id)
  }
}

/** Ignores the no-op `dimensions` changes React Flow fires every frame. */
export function hasChanges(changes: NodeChange<Node>[], nodes: Node[]) {
  const findNode = nodeLookup(nodes)

  return changes.some((change) => {
    if (change.type !== 'dimensions') return true

    const node = findNode(change.id)

    if (!node) return true

    return (
      node.resizing !== change.resizing ||
      node.width !== change.dimensions?.width ||
      node.height !== change.dimensions?.height
    )
  })
}

export function applyChanges(changes: NodeChange<Node>[], nodes: Node[]) {
  // all changes for an id, in order: NodeResizer emits position + dimensions
  // for the same node in one call
  const changesById = new Map<string, NodeChange<Node>[]>()
  for (const change of changes) {
    if (!('id' in change)) continue

    const existing = changesById.get(change.id)

    if (existing) existing.push(change)
    else changesById.set(change.id, [change])
  }

  const applied: Node[] = []

  for (const node of nodes) {
    const nodeChanges = changesById.get(node.id)

    if (!nodeChanges) {
      applied.push(node)
      continue
    }

    let next: Node | null = node

    for (const change of nodeChanges) {
      if (!next) break
      next = applyChange(change, next)
    }

    if (next) applied.push(next)
  }

  // Add new nodes
  const additions = changes.filter(
    (c): c is NodeAddChange<Node> => c.type === 'add',
  )
  for (const addition of additions) {
    if (addition.index !== undefined)
      applied.splice(addition.index, 0, { ...addition.item })
    else applied.push({ ...addition.item })
  }

  return applied
}

function applyChange(change: NodeChange<Node>, node: Node): Node | null {
  switch (change.type) {
    case 'remove':
      return null
    case 'position':
      return {
        ...node,
        position: change.position ?? node.position,
        dragging: change.dragging ?? node.dragging,
      }
    case 'replace':
      return { ...change.item }
    case 'select':
      return {
        ...node,
        selected: change.selected ?? node.selected,
      }
    case 'dimensions':
      if (!change.dimensions)
        return {
          ...node,
          resizing: change.resizing ?? node.resizing,
        }

      const { dimensions } = change
      const measured = {
        width: dimensions.width ?? node.measured?.width ?? node.width,
        height: dimensions.height ?? node.measured?.height ?? node.height,
      }

      return {
        ...node,
        measured,
        width: change.setAttributes ? dimensions.width : node.width,
        height: change.setAttributes ? dimensions.height : node.height,
        resizing: change.resizing ?? node.resizing,
      }
    default:
      return node
  }
}

// Make sure group-nodes are sorted before others to allow parenting
export const sortNodes = (a: Node, b: Node): number => {
  if (a.type === b.type) {
    return 0
  }
  return a.type === 'group' && b.type !== 'group' ? -1 : 1
}
